import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Music2, ArrowRight, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(["booker", "conductor"].includes(params.get("role")) ? params.get("role") : "musician");
  const [busy, setBusy] = useState(false);
  const [refCode, setRefCode] = useState((params.get("ref") || "").toUpperCase());
  const [inviter, setInviter] = useState(null);
  const navigate = useNavigate();

  // Resolve referral code → inviter name
  useEffect(() => {
    if (!refCode) { setInviter(null); return; }
    let cancelled = false;
    api.get(`/referrals/lookup/${encodeURIComponent(refCode)}`)
      .then(({ data }) => { if (!cancelled) setInviter(data.inviter_name); })
      .catch(() => { if (!cancelled) setInviter(null); });
    return () => { cancelled = true; };
  }, [refCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name, email, password, role };
    if (refCode) payload.ref_code = refCode;
    const res = await register(payload);
    setBusy(false);
    if (res.ok) {
      if (res.requiresVerification) {
        // New post-Phase-2 flow: route to the check-your-email screen.
        navigate("/check-email", {
          replace: true,
          state: { email: res.email || email },
        });
        return;
      }
      toast.success(inviter ? `Welcome — ${inviter} invited you.` : "Account created");
      navigate(role === "musician" ? "/profile" : "/search", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grid place-items-center p-4">
      <div className="w-full max-w-md card-base p-6 sm:p-8 animate-fade-up" data-testid="register-card">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-xl bg-sage-500 text-white grid place-items-center"><Music2 size={20} /></span>
          <span className="font-heading font-semibold text-lg">AMDS</span>
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Create your AMDS account</h1>
        <p className="text-ink-600 text-sm mt-1">Friendly. Practical. No spam.</p>

        {inviter && (
          <div className="mt-4 flex items-start gap-3 rounded-xl bg-sage-50 border border-sage-200 p-3" data-testid="invite-banner">
            <Gift size={18} className="text-sage-600 mt-0.5 shrink-0" />
            <div className="text-sm text-sage-800">
              <strong>{inviter}</strong> invited you to AMDS. Sign up with code <span className="font-mono font-semibold">{refCode}</span> and they'll get a thank-you credit.
            </div>
          </div>
        )}

        <div className="mt-5">
          <label className="label-overline block mb-2">I'm signing up as</label>
          <div className="grid grid-cols-3 gap-1 p-1 bg-ivory-100 rounded-full" data-testid="register-role-toggle">
            {[{ v: "musician", label: "Musician" }, { v: "booker", label: "Booker" }, { v: "conductor", label: "Conductor" }].map((r) => (
              <button
                key={r.v} type="button" onClick={() => setRole(r.v)}
                data-testid={`register-role-${r.v}`}
                className={`rounded-full py-2 text-sm font-medium transition-all ${role === r.v ? "bg-sage-500 text-white shadow-soft" : "text-ink-700 hover:bg-white/60"}`}
              >{r.label}</button>
            ))}
          </div>
          <p className="text-xs text-ink-600 mt-2" data-testid="role-explainer">
            {role === "musician" ? (
              <>Musicians get the dep finder, availability diary and booking inbox — scoped to your own instrument.</>
            ) : (
              <>Bookers / conductors get cross-instrument search and event creation. Advanced ensemble management arrives post-beta.</>
            )}
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <div>
            <label className="label-overline block mb-2">Your name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="input-field" placeholder="Anna Whittaker" data-testid="register-name-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="you@example.com" data-testid="register-email-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-field" placeholder="At least 6 characters" data-testid="register-password-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Invite code <span className="text-ink-400 normal-case">(optional)</span></label>
            <input
              value={refCode}
              onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20))}
              className="input-field uppercase tracking-wider"
              placeholder="e.g. MARYVIOLA"
              data-testid="register-refcode-input"
            />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="register-submit-btn">
            {busy ? "Creating…" : (<>Create account <ArrowRight size={16} /></>)}
          </button>
        </form>

        <div className="mt-6 text-sm text-ink-600">
          Already have one? <Link to="/login" className="text-sage-600 hover:text-sage-700" data-testid="register-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
